import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { flushCloudState, hydrateCloudState, resetCloudState } from "@/lib/cloudStore";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isDemoAccount: boolean;
  /** True once this learner's cloud record has been pulled. */
  stateReady: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);


export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  const [stateReady, setStateReady] = useState(false);

  useEffect(() => {
    const LAST_UID_KEY = "wekit_last_uid";
    const clearStaleIfUserChanged = (newUid: string | null) => {
      const prev = localStorage.getItem(LAST_UID_KEY);
      if (newUid && prev && prev !== newUid) {
        ["wekit_assessment_id", "wekit_trait_scores", "wekit_passion_ids", "wekit_quiz_boost"].forEach((k) =>
          localStorage.removeItem(k),
        );
      }
      if (newUid) localStorage.setItem(LAST_UID_KEY, newUid);
    };

    const loadDemoFlag = async (uid: string | null) => {
      if (!uid) { setIsDemoAccount(false); return; }
      const { data } = await (supabase as any)
        .from("profiles")
        .select("is_demo_account")
        .eq("id", uid)
        .maybeSingle();
      setIsDemoAccount(Boolean(data?.is_demo_account));
    };

    /**
     * The learner's record lives in the database, so it has to be in memory
     * before any surface reads it. Signing out drops it: the next person on
     * this device must never inherit it.
     */
    const syncCloudState = async (uid: string | null) => {
      if (!uid) { resetCloudState(); setStateReady(false); return; }
      await hydrateCloudState(uid);
      setStateReady(true);
    };

    // Set up listener FIRST to avoid missed events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      clearStaleIfUserChanged(newSession?.user?.id ?? null);
      // Defer follow-up reads to avoid deadlocks inside the auth callback.
      setTimeout(() => {
        void loadDemoFlag(newSession?.user?.id ?? null);
        void syncCloudState(newSession?.user?.id ?? null);
      }, 0);
    });

    // THEN fetch existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      clearStaleIfUserChanged(session?.user?.id ?? null);
      Promise.all([
        loadDemoFlag(session?.user?.id ?? null),
        syncCloudState(session?.user?.id ?? null),
      ]).finally(() => setLoading(false));
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Push anything still queued before we lose the session that can write it.
    await flushCloudState();
    resetCloudState();
    // Don't leave a copy of the report on a shared device.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("wekit_report_cache_v1:"))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* storage unavailable */ }
    setStateReady(false);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isDemoAccount, stateReady, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
